import { RSAA } from 'redux-api-middleware'
import { getApiPath } from 'sidecar-utility-library'
import { convertToISOString } from 'sidecar-utilities'

import { types, initialState } from './types'

/**
 * Fetch recurring expense form data from the expense-form endpoint This
 * retrieves previously saved form data including bundle info
 *
 * @param {string} expenseUuid - The UUID of the expense
 * @returns RSAA action for fetching recurring expense form
 */
export const fetchRecurringExpenseForm = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${expenseUuid}/expense-form?formType=RECURRING_EXPENSE`,
    ),
    method: 'GET',
    types: [
      types.FETCH_RECURRING_EXPENSE_FORM_REQUEST,
      types.FETCH_RECURRING_EXPENSE_FORM_SUCCESS,
      types.FETCH_RECURRING_EXPENSE_FORM_ERROR,
    ],
  },
})

/**
 * Save recurring expense form data to the expense-form endpoint This should be
 * called before submitting the recurring expense
 *
 * @param {string} expenseUuid - The UUID of the expense
 * @param {string} visitDate - The date of the visit in MM/DD/YYYY format
 * @param {object} bundleInfo - Optional bundle info for the Price Lock
 * @returns RSAA action for saving recurring expense form
 */
export const saveRecurringExpenseForm = (
  expenseUuid,
  visitDate,
  bundleInfo,
) => {
  const formFields = []

  // Add service date
  if (visitDate) {
    formFields.push({
      field: 'SERVICE_DATE',
      value: convertToISOString(visitDate),
    })
  }

  // Add bundle info if provided
  if (bundleInfo != null) {
    formFields.push({
      field: 'BUNDLE_INFO',
      value: JSON.stringify(bundleInfo),
    })
  }

  return {
    [RSAA]: {
      endpoint: getApiPath('exp', `expenses/${expenseUuid}/expense-form`),
      method: 'PUT',
      body: JSON.stringify({
        formType: 'RECURRING_EXPENSE',
        formFields,
      }),
      types: [
        types.SAVE_RECURRING_EXPENSE_FORM_REQUEST,
        {
          type: types.SAVE_RECURRING_EXPENSE_FORM_SUCCESS,
          meta: { expenseUuid },
        },
        types.SAVE_RECURRING_EXPENSE_FORM_ERROR,
      ],
    },
  }
}

export const clearRecurringExpenseFormError = () => ({
  type: types.CLEAR_RECURRING_EXPENSE_FORM_ERROR,
})

export const clearRecurringExpenseFormData = () => ({
  type: types.CLEAR_RECURRING_EXPENSE_FORM_DATA,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.SAVE_RECURRING_EXPENSE_FORM_REQUEST:
    case types.FETCH_RECURRING_EXPENSE_FORM_REQUEST: {
      return {
        ...state,
        recurringExpenseForm: {
          ...state.recurringExpenseForm,
          busy: true,
          error: null,
        },
      }
    }

    case types.SAVE_RECURRING_EXPENSE_FORM_SUCCESS:
    case types.FETCH_RECURRING_EXPENSE_FORM_SUCCESS: {
      return {
        ...state,
        recurringExpenseForm: {
          ...state.recurringExpenseForm,
          busy: false,
          entity: payload,
          error: null,
        },
      }
    }

    case types.SAVE_RECURRING_EXPENSE_FORM_ERROR:
    case types.FETCH_RECURRING_EXPENSE_FORM_ERROR: {
      return {
        ...state,
        recurringExpenseForm: {
          ...state.recurringExpenseForm,
          busy: false,
          error:
            payload?.response?.message ||
            payload?.message ||
            'An error occurred',
        },
      }
    }

    case types.CLEAR_RECURRING_EXPENSE_FORM_ERROR: {
      return {
        ...state,
        recurringExpenseForm: {
          ...state.recurringExpenseForm,
          error: null,
        },
      }
    }

    case types.CLEAR_RECURRING_EXPENSE_FORM_DATA: {
      return {
        ...state,
        recurringExpenseForm: {
          busy: false,
          entity: {},
          error: null,
        },
      }
    }

    default:
      return state
  }
}
